import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Node as PmNode } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import type { Document, Msg } from "../types";
import { parse } from "./serialization";
import { schema } from "./schema";
import { buildPlugins } from "./plugins";
import { previewNodeViews } from "./node-views";

// A preview is the editor in read-only mode. It uses the same schema, so it
// renders like the editor. `previewNodeViews` limits how deep previews nest.

type Dispatch = (msg: Msg) => void;

// A preview cannot save, so a notice can replace content that the editor
// cannot read.
function previewDoc(content: Document): PmNode {
  try {
    return parse(content);
  } catch {
    const notice = schema.text("⚠ The editor cannot read this card.");
    return schema.node("doc", null, [schema.node("paragraph", null, [notice])]);
  }
}

// Keep only the first `maxLines` top-level blocks. Each block — paragraph,
// heading, list, view block — counts as one line. Returns whether anything was
// dropped so the caller can show an ellipsis.
function truncateContent(content: Document, maxLines?: number): { content: Document; truncated: boolean } {
  if (maxLines == null || content.blocks.length <= maxLines) {
    return { content, truncated: false };
  }
  return { content: { blocks: content.blocks.slice(0, maxLines) }, truncated: true };
}

// Mount a read-only editor for `content` into `el`. Returns the view so the
// caller can destroy it. When `maxLines` is set, content past that many
// top-level blocks is cut and a trailing ellipsis is appended.
export function renderCardPreview(
  el: HTMLElement,
  content: Document,
  depth: number,
  dispatch: Dispatch,
  maxLines?: number
): EditorView {
  el.classList.add("card-preview-editor");
  const { content: shown, truncated } = truncateContent(content, maxLines);
  const view = new EditorView(el, {
    state: EditorState.create({ doc: previewDoc(shown), plugins: buildPlugins({ suggest: false }) }),
    editable: () => false,
    nodeViews: previewNodeViews(depth, dispatch, (inner, card) => renderCardPreview(inner, card, depth + 1, dispatch)),
  });
  if (truncated) {
    const ell = document.createElement("div");
    ell.className = "preview-ellipsis";
    ell.textContent = "…";
    el.appendChild(ell);
  }
  return view;
}

// A snabbdom node that hosts a read-only preview editor, managing its lifecycle
// across insert/update/destroy. Fills the space it is given.
const hostViews = new WeakMap<Element, EditorView>();

export function previewHost(
  content: Document,
  depth: number,
  dispatch: Dispatch,
  maxLines?: number
): VNode {
  const mount = (el: HTMLElement) => {
    hostViews.set(el, renderCardPreview(el, content, depth, dispatch, maxLines));
  };
  const unmount = (el: HTMLElement) => {
    hostViews.get(el)?.destroy();
    hostViews.delete(el);
    // renderCardPreview may have appended an ellipsis sibling; clear it too.
    el.querySelector(".preview-ellipsis")?.remove();
  };
  return h("div.card-preview-host", {
    hook: {
      insert: (vnode) => mount(vnode.elm as HTMLElement),
      update: (_old, vnode) => {
        const el = vnode.elm as HTMLElement;
        const view = hostViews.get(el);
        // Compare against the truncated doc that the view holds. The truncation
        // and the ellipsis prevent a partial updateState, so mount a new view.
        const shown = previewDoc(truncateContent(content, maxLines).content);
        if (view && shown.eq(view.state.doc)) return;
        unmount(el);
        mount(el);
      },
      destroy: (vnode) => unmount(vnode.elm as HTMLElement),
    },
  });
}
